// برنامه/طرح های دستگاههای اجرایی
//
// یک صفحه با دو حالت (prop `programType`): ۰ = «برنامه های دستگاههای اجرایی»،
// ۱ = «اعتبارمصوب، تخصیص طرح های دستگاههای اجرایی». عنوان و برچسب‌ها
// («برنامه» ⇄ «طرح») با همین سوییچ عوض می‌شوند.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { usePager, CountBadge, MoreButton } from '../../components/paging';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import { useFormButtons } from '../../utils/vbPerms';
import { jsonOf, netErr } from '../../utils/http';

const toFa   = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const fmtNum = n => (n ? Number(n).toLocaleString('fa-IR') : '•');
const fmtAmt = n => Number(n || 0).toLocaleString('fa-IR');

const API = (path, opts = {}) => fetch(`/api/treasury${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

const EMPTY = () => ({
  department_id: 0, department: '',
  credit_id: 0, credit_title: '',
  program_id: 0, program: '',
  remark: '',
  letter_no: '', letter_date: '', credit_pay: '', allocate_pay: '',
});

/* جستجوی انتخابی — یک‌دست با سایر فرم‌های برنامه */
function Lookup({ label, value, endpoint, onPick, flex = 1, disabled }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const search = async (text) => {
    setQ(text);
    const j = await API(`${endpoint}${endpoint.includes('?') ? '&' : '?'}q=${encodeURIComponent(text)}`);
    if (j.ok) { setRows(j.rows || []); setOpen(true); }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex, position: 'relative' }}>
      <label style={ui.mlabel}>{label}</label>
      <input value={value || q} disabled={disabled} onFocus={() => !disabled && search(q)}
        onChange={e => search(e.target.value)} placeholder="جستجو..." style={ui.input} />
      {value && !disabled && <button onClick={() => { onPick({ id: 0, display: '' }); setQ(''); }}
        style={{ position: 'absolute', left: '6px', top: '30px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#c62828', fontSize: '14px' }}>✕</button>}
      {open && rows.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', right: 0, left: 0, background: '#fff', border: '1px solid #e0e0e0',
          borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 700, maxHeight: '220px', overflowY: 'auto', marginTop: '2px' }}>
          {rows.map(r => (
            <div key={r.id} onClick={() => { onPick(r); setOpen(false); setQ(''); }}
              style={{ padding: '8px 10px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #f0f0f0' }}
              onMouseEnter={e => e.currentTarget.style.background = '#e8f0fe'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>{r.display}</div>
          ))}
        </div>
      )}
    </div>
  );
}

/* یک کارت آمار */
function StatCard({ title, d }) {
  if (!d) return null;
  const color = v => (Number(v) < 0 ? '#c62828' : '#1b5e20');
  const Row = ({ k, v, bold }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', padding: '2px 0',
      fontWeight: bold ? 700 : 400, borderTop: bold ? '1px solid #e0e0e0' : 'none' }}>
      <span style={{ color: '#666' }}>{k}</span>
      <span style={{ fontFamily: 'monospace', color: bold ? color(v) : '#111' }}>{fmtAmt(v)}</span>
    </div>
  );
  return (
    <div style={{ flex: 1, minWidth: '230px', background: '#fff', border: '1px solid #e0e0e0',
      borderRadius: '10px', padding: '10px 12px', fontSize: '11.5px' }}>
      <div style={{ fontWeight: 800, color: '#1a2f50', marginBottom: '6px', fontSize: '12.5px' }}>{title}</div>
      <Row k="اعتبار مصوب" v={d.credit} />
      <Row k="تخصیص نقدی" v={d.allocate} />
      <Row k="تخصیص اسناد" v={d.document_allocate} />
      <Row k="دریافتی نقد" v={d.received_cash} />
      <Row k="اسناد دریافتی" v={d.received_document} />
      <Row k="پرداختی نقد به دستگاه" v={d.cash_paid} />
      <Row k="پرداختی تنخواه به دستگاه" v={d.fund_paid} />
      <Row k="پرداختی اسناد خزانه" v={d.document_paid} />
      <Row k="جذب دستگاه" v={d.department_paid} />
      <Row k="مانده نقد" v={d.remain_cash} bold />
      <Row k="مانده اسناد" v={d.remain_document} bold />
      <Row k="مانده تخصیص" v={d.remain_allocate} bold />
      <Row k="مانده جذب" v={d.remain} bold />
    </div>
  );
}

export default function TreasuryProgramList({ user, onBack, programType = 0 }) {
  // دکمه‌های فرم FRMTREASURYPROGRAMS؛ دیدن = دسترسی
  const vbf = useFormButtons('FRMTREASURYPROGRAMS', ["BTNNEW","BTNEDIT","BTNDELETE","BTNOK"]);
  const PT      = programType === 1 ? 1 : 0;
  const PWORD   = PT === 1 ? 'طرح' : 'برنامه';                 // «برنامه» یا «طرح» در برچسب‌ها
  const PTITLE  = PT === 1 ? 'اعتبارمصوب، تخصیص طرح های دستگاههای اجرایی'
                           : 'برنامه های دستگاههای اجرایی';
  const ptQS    = `program_type=${PT}`;
  const [data, setData]         = useState([]);
  const pager  = usePager();         // صفحه‌بندی فهرست برنامه‌ها — ۲۰۰ ردیف
  const dPager = usePager();         // صفحه‌بندی گرید گردش — ۲۰۰ ردیف
  const [year, setYear]         = useState(0);
  const [years, setYears]       = useState([]);
  const [showAll, setShowAll]   = useState(false);   // «همه برنامه ها»
  const [search, setSearch]     = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail]     = useState([]);
  const [stats, setStats]       = useState(null);
  const [liveStats, setLiveStats] = useState(false); // آمار زنده با انتخاب ردیف
  const [loading, setLoading]   = useState(false);
  const [modal, setModal]       = useState(null);
  const [form, setForm]         = useState(EMPTY());
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [toast, setToast]       = useState('');

  const showToast = m => { setToast(m); setTimeout(() => setToast(''), 3000); };

  // ستون‌های گرید اصلی
  const cols = ['دستگاه اجرایی', 'منبع اعتبار', PWORD, 'تراکنش', 'اعتبار مصوب', 'تخصیص نقدی',
    'تخصیص اسناد', 'دریافتی نقد', 'اسناد دریافتی', 'پرداختی نقد به دستگاه اجرایی',
    'پرداختی تنخواه به دستگاه اجرایی', 'پرداختی اسناد خزانه به دستگاه اجرایی',
    'هزینه قطعی دستگاه', 'تنخواه پرداختی دستگاه', 'پیش پرداخت پرداختی دستگاه',
    'علی الحساب پرداختی دستگاه', 'توضیح'];
  const initW = [200, 190, 190, 70, 120, 120, 120, 120, 120, 150, 150, 160, 130, 140, 150, 150, 160];
  const [colW, setColW] = useState(initW);
  const colWRef = useRef(initW);
  useEffect(() => { colWRef.current = colW; }, [colW]);
  const startResize = useCallback((idx, e) => {
    e.preventDefault(); e.stopPropagation();
    const sx = e.clientX, sw = colWRef.current[idx];
    const onMove = ev => setColW(w => { const n = [...w]; n[idx] = Math.max(40, sw + (sx - ev.clientX)); return n; });
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
  }, []);

  const qs = (extra = {}) => {
    const p = new URLSearchParams({ all: showAll ? 1 : 0, search, page: 1, ...extra });
    if (year) p.set('year', year);
    return p.toString();
  };

  const load = useCallback(async (opts = {}) => {
    setLoading(true); setError(''); setSelected(null); setDetail([]); setStats(null);
    const p = new URLSearchParams({
      all: (opts.all ?? showAll) ? 1 : 0,
      search: opts.search ?? search,
      page: opts.page ?? 1,
    });
    const y = opts.year ?? year;
    if (y) p.set('year', y);
    const j = await API(`/programs/?${p.toString()}&${ptQS}`);
    setLoading(false);
    if (!j.ok) { setError(j.msg); return; }
    setData(prev => pager.merge(prev, j, opts.page ?? 1));
    if (!year && j.year) setYear(j.year);
  }, [showAll, search, year]);

  useEffect(() => {
    (async () => {
      const j = await API('/programs/years/');
      if (j.ok) setYears(j.data);
      load({ page: 1 });
    })();
  }, []);   // eslint-disable-line

  // انتخاب ردیف → گردش + آمار
  const pick = async (r) => {
    if (selected?.id === r.id) { setSelected(null); setDetail([]); setStats(null); return; }
    setSelected(r); setDetail([]); setStats(null);
    const [d, s] = await Promise.all([
      API(`/programs/${r.id}/turnovers/?page=1&${ptQS}${year ? `&year=${year}` : ''}`),
      API(`/programs/${r.id}/statistics/?live=${liveStats ? 1 : 0}&${ptQS}${year ? `&year=${year}` : ''}`),
    ]);
    if (d.ok) setDetail(prev => dPager.merge(prev, d, 1));
    if (s.ok) setStats(s.data);
  };

  // صفحهٔ بعدی گرید گردش
  const moreDetail = async (p) => {
    if (!selected) return;
    const d = await API(`/programs/${selected.id}/turnovers/?page=${p}&${ptQS}${year ? `&year=${year}` : ''}`);
    if (d.ok) setDetail(prev => dPager.merge(prev, d, p));
  };

  // تغییر گزینهٔ آمار → بازخوانی آمار همان ردیف
  const toggleStats = async () => {
    const next = !liveStats;
    setLiveStats(next);
    if (selected) {
      const s = await API(`/programs/${selected.id}/statistics/?live=${next ? 1 : 0}&${ptQS}${year ? `&year=${year}` : ''}`);
      if (s.ok) setStats(s.data);
    }
  };

  const F = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const openNew  = () => { if (!vbf.can('BTNNEW')) return; setForm(EMPTY()); setError(''); setModal('new'); };
  const openEdit = () => { if (!vbf.can('BTNEDIT')) return;
    if (!selected) return;
    setForm({
      department_id: selected.department_id, department: selected.department,
      credit_id: selected.credit_id, credit_title: selected.credit_title,
      program_id: selected.program_id, program: selected.program,
      remark: selected.remark,
      letter_no: '', letter_date: '', credit_pay: '', allocate_pay: '',
    });
    setError(''); setModal('edit');
  };
  const openDel = () => { if (!vbf.can('BTNDELETE')) return; if (selected) setModal('delete'); };

  const save = async () => {
    setSaving(true); setError('');
    const body = JSON.stringify(form);
    const j = modal === 'new'
      ? await API('/programs/create/', { method: 'POST', body })
      : await API(`/programs/${selected.id}/update/`, { method: 'PUT', body });
    setSaving(false);
    if (j.ok) { setModal(null); showToast(j.msg); load({ page: 1 }); }
    else setError(j.msg);
  };

  const doDelete = async () => {
    setSaving(true);
    const j = await API(`/programs/${selected.id}/delete/`, { method: 'DELETE' });
    setSaving(false);
    if (j.ok) { setModal(null); showToast('حذف شد'); load({ page: 1 }); }
    else { setError(j.msg); setModal(null); }
  };

  const toolbar = (
    <>
      <div style={ui.toolBtns}>
        {vbf.can('BTNNEW') && <button onClick={openNew} style={ui.btnNew}>＋ جدید</button>}
        {vbf.can('BTNEDIT') && <button onClick={openEdit} disabled={!selected} style={{ ...ui.btnEdit, opacity: selected ? 1 : 0.4, cursor: selected ? 'pointer' : 'default' }}>✎ ویرایش</button>}
        {vbf.can('BTNDELETE') && <button onClick={openDel} disabled={!selected} style={{ ...ui.btnDel, opacity: selected ? 1 : 0.4, cursor: selected ? 'pointer' : 'default' }}>✕ حذف</button>}
      </div>
      <select value={year || ''} onChange={e => { const y = Number(e.target.value); setYear(y); load({ year: y, page: 1 }); }}
        style={{ ...ui.input, width: '110px' }} title="سال مالی">
        {years.length === 0 && <option value="">سال مالی</option>}
        {years.map(y => <option key={y} value={y}>{toFa(y)}</option>)}
      </select>
      <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
        <input type="checkbox" checked={showAll} onChange={e => { setShowAll(e.target.checked); load({ all: e.target.checked, page: 1 }); }} />
        {`همه ${PWORD} ها`}
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
        <input type="checkbox" checked={liveStats} onChange={toggleStats} />
        آمار
      </label>
      <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load({ page: 1 })}
        style={{ ...ui.searchInput, flex: 1, minWidth: '160px' }} placeholder={`جستجو (دستگاه، منبع، ${PWORD}، ابلاغ، مبلغ)...`} />
      {loading && <span>⏳</span>}
      <CountBadge shown={data.length} total={pager.total} loading={loading} />
    </>
  );

  return (
    <>
      <ListPage user={user} onBack={onBack} title={PTITLE} subtitle="بخش اعتبارات خزانه — سامانه ارجان"
        toolbar={toolbar} error={!modal ? error : ''}
        pager={pager} loading={loading} onMore={p => load({ page: p })}>
        <div style={ui.splitWrap}>
        <div style={ui.topPane}>
        <table style={{ ...ui.table, tableLayout: 'fixed' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
            <tr>
              {cols.map((h, i) => (
                <th key={i} style={{ ...ui.th, width: colW[i], minWidth: 40, position: 'relative', userSelect: 'none' }}>
                  {h}<span onMouseDown={e => startResize(i, e)} style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', cursor: 'col-resize' }} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((r, i) => {
              const sel = selected?.id === r.id;
              const bg  = sel ? '#1a2f50' : i % 2 === 0 ? '#fff' : '#f7f2e7';   // ردیف‌های یک‌درمیان کرم
              const tc  = sel ? '#fff' : '#111';
              const num = { ...ui.tdNum, color: sel ? '#fff' : '#111' };
              return (
                <tr key={r.id} style={{ background: bg, cursor: 'pointer' }}
                  onClick={() => pick(r)} onDoubleClick={() => { setSelected(r); setTimeout(openEdit, 0); }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = '#e8f0fe'; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = bg; }}>
                  <td style={{ ...ui.td, textAlign: 'right', color: tc, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.department}>{r.department || '•'}</td>
                  <td style={{ ...ui.td, textAlign: 'right', color: tc, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.credit_title}>{r.credit_title || '•'}</td>
                  <td style={{ ...ui.td, textAlign: 'right', color: tc, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.program}>{r.program || '•'}</td>
                  <td style={{ ...ui.td, color: tc }}>{toFa(r.cnt)}</td>
                  <td style={num}>{fmtNum(r.credit)}</td>
                  <td style={num}>{fmtNum(r.allocate)}</td>
                  <td style={num}>{fmtNum(r.document_allocate)}</td>
                  <td style={num}>{fmtNum(r.cash_received)}</td>
                  <td style={num}>{fmtNum(r.document_received)}</td>
                  <td style={num}>{fmtNum(r.cash_paid)}</td>
                  <td style={num}>{fmtNum(r.fund_paid)}</td>
                  <td style={num}>{fmtNum(r.document_paid)}</td>
                  <td style={num}>{fmtNum(r.dep_cash_paid)}</td>
                  <td style={num}>{fmtNum(r.dep_fund_paid)}</td>
                  <td style={num}>{fmtNum(r.dep_prepayment)}</td>
                  <td style={num}>{fmtNum(r.dep_onaccount)}</td>
                  <td style={{ ...ui.td, textAlign: 'right', color: tc, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.remark}>{r.remark || '•'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && data.length === 0 && <div style={ui.empty}>📭 موردی یافت نشد</div>}
        </div>

        {/* پنل پایین — آمار و گردش برنامه، همیشه در دید */}
        <div style={ui.bottomPane}>
        {!selected && (
          <div style={{ ...ui.empty, padding: '18px' }}>
            {`برای دیدن آمار و گردش ${PWORD}، یک ردیف را از جدول بالا انتخاب کنید`}
          </div>
        )}

        {/* آمار چهارگانه */}
        {selected && stats && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', padding: '12px 4px' }}>
            <StatCard title="دستگاه اجرایی" d={stats.department} />
            <StatCard title="منبع اعتبار" d={stats.credit} />
            <StatCard title="دستگاه + منبع اعتبار" d={stats.department_credit} />
            <StatCard title={PWORD} d={stats.program} />
          </div>
        )}

        {/* گردش برنامهٔ انتخاب‌شده */}
        {selected && detail.length > 0 && (
          <div style={{ padding: '4px' }}>
            <div style={{ fontSize: '12.5px', fontWeight: 800, color: '#1a2f50', margin: '6px 2px' }}>
              {`گردش ${PWORD}`} — {detail.length < dPager.total
                ? `${toFa(detail.length)} از ${toFa(dPager.total)} ردیف`
                : `${toFa(detail.length)} ردیف`}
            </div>
            <div style={{ overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
              <table style={{ ...ui.table, minWidth: '1500px' }}>
                <thead>
                  <tr>{['شماره', 'تاریخ', 'شماره ابلاغ', 'تاریخ ابلاغ', 'افزایش/کاهش اعتبار', 'اعتبار مصوب',
                    'افزایش/کاهش تخصیص نقدی', 'تخصیص نقدی', 'افزایش/کاهش تخصیص اسناد', 'تخصیص اسناد',
                    'دریافتی نقد', 'دریافتی اسناد', 'نوع اسناد دریافتی', 'تاریخ صدور', 'کد رهگیری',
                    'سریال پرداخت', 'شماره چک', 'تاریخ چک', 'پرداختی نقد', 'پرداختی تنخواه', 'اسناد پرداختی',
                    'نوع اسناد پرداختی', 'هزینه قطعی دستگاه', 'اسناد واگذاری دستگاه', 'تنخواه پرداختی دستگاه',
                    'پیش پرداخت دستگاه', 'علی الحساب پرداختی دستگاه', 'پیمانکار', 'شناسه ملی', 'شرح']
                    .map((h, i) => <th key={i} style={{ ...ui.th, whiteSpace: 'nowrap' }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {detail.map((d, i) => (
                    <tr key={d.id} style={{ background: i % 2 === 0 ? '#fff' : '#faf7ef' }}>
                      <td style={ui.td}>{toFa(d.serial_no)}</td>
                      <td style={{ ...ui.td, fontFamily: 'monospace', fontSize: '11px' }}>{d.serial_date || '•'}</td>
                      <td style={ui.td}>{toFa(d.letter_no) || '•'}</td>
                      <td style={{ ...ui.td, fontFamily: 'monospace', fontSize: '11px' }}>{d.letter_date || '•'}</td>
                      <td style={ui.tdNum}>{fmtNum(d.credit)}</td>
                      <td style={ui.tdNum}>{fmtNum(d.total_credit)}</td>
                      <td style={ui.tdNum}>{fmtNum(d.allocate)}</td>
                      <td style={ui.tdNum}>{fmtNum(d.total_allocate)}</td>
                      <td style={ui.tdNum}>{fmtNum(d.document_allocate)}</td>
                      <td style={ui.tdNum}>{fmtNum(d.total_document_allocate)}</td>
                      <td style={ui.tdNum}>{fmtNum(d.cash_received)}</td>
                      <td style={ui.tdNum}>{fmtNum(d.document_received)}</td>
                      <td style={{ ...ui.td, textAlign: 'right' }}>{d.received_document_title || '•'}</td>
                      <td style={{ ...ui.td, fontFamily: 'monospace', fontSize: '11px' }}>{d.document_issue_date || '•'}</td>
                      <td style={ui.td}>{toFa(d.document_refrence_code) || '•'}</td>
                      <td style={ui.td}>{toFa(d.pay_serial_no) || '•'}</td>
                      <td style={ui.td}>{toFa(d.cheque_no) || '•'}</td>
                      <td style={{ ...ui.td, fontFamily: 'monospace', fontSize: '11px' }}>{d.cheque_date || '•'}</td>
                      <td style={ui.tdNum}>{fmtNum(d.cash_paid)}</td>
                      <td style={ui.tdNum}>{fmtNum(d.fund_paid)}</td>
                      <td style={ui.tdNum}>{fmtNum(d.document_paid)}</td>
                      <td style={{ ...ui.td, textAlign: 'right' }}>{d.paid_document_title || '•'}</td>
                      <td style={ui.tdNum}>{fmtNum(d.dep_cash_paid)}</td>
                      <td style={ui.tdNum}>{fmtNum(d.dep_document_paid)}</td>
                      <td style={ui.tdNum}>{fmtNum(d.dep_fund_paid)}</td>
                      <td style={ui.tdNum}>{fmtNum(d.dep_prepayment)}</td>
                      <td style={ui.tdNum}>{fmtNum(d.dep_onaccount)}</td>
                      <td style={{ ...ui.td, textAlign: 'right' }}>{d.contractor || '•'}</td>
                      <td style={ui.td}>{toFa(d.contractor_national_code) || '•'}</td>
                      <td style={{ ...ui.td, textAlign: 'right' }} title={d.remark}>{d.remark || '•'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <MoreButton pager={dPager} loading={loading} onMore={moreDetail} />
          </div>
        )}
        </div>
        </div>
      </ListPage>

      {(modal === 'new' || modal === 'edit') && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: '760px' }} onClick={e => e.stopPropagation()}>
            <div style={ui.mHeader}>
              <div style={ui.mTitle}>{modal === 'new' ? `＋ ${PWORD} جدید` : `✎ ویرایش ${PWORD}`}</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={ui.mBody}>
              {error && <div style={ui.errBox}>⚠️ {error}</div>}
              <div style={ui.row}>
                <Lookup label="دستگاه اجرایی" value={form.department} endpoint="/programs/lookup/department/"
                  onPick={r => setForm(f => ({ ...f, department_id: r.id, department: r.display, program_id: 0, program: '' }))} />
              </div>
              <div style={ui.row}>
                <Lookup label="منبع اعتبار" value={form.credit_title} endpoint="/programs/lookup/credit/"
                  onPick={r => setForm(f => ({ ...f, credit_id: r.id, credit_title: r.display }))} />
              </div>
              <div style={ui.row}>
                <Lookup label={PWORD} value={form.program}
                  endpoint={`/programs/lookup/program/?department_id=${form.department_id}${year ? `&year=${year}` : ''}`}
                  onPick={r => setForm(f => ({ ...f, program_id: r.id, program: r.display }))} />
              </div>
              <div style={ui.row}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>توضیح</label>
                  <input value={form.remark} onChange={e => F('remark', e.target.value)} style={ui.input} />
                </div>
              </div>

              <div style={ui.divider} />
              <div style={{ fontSize: '12px', color: '#666', margin: '0 0 6px' }}>
                ابلاغ اعتبار (اختیاری) — در صورت تکمیل شماره و تاریخ ابلاغ و یکی از دو مبلغ، یک ردیف گردش هم ثبت می‌شود.
              </div>
              <div style={ui.row}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>شماره ابلاغ</label>
                  <input value={form.letter_no} onChange={e => F('letter_no', e.target.value)} style={ui.input} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>تاریخ ابلاغ</label>
                  <JalaliDatePicker value={form.letter_date} onChange={v => F('letter_date', v)} placeholder="تاریخ ابلاغ" />
                </div>
              </div>
              <div style={ui.row}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>افزایش/کاهش اعتبار</label>
                  <input type="number" value={form.credit_pay} onChange={e => F('credit_pay', e.target.value)} style={ui.input} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>افزایش/کاهش تخصیص</label>
                  <input type="number" value={form.allocate_pay} onChange={e => F('allocate_pay', e.target.value)} style={ui.input} />
                </div>
              </div>
            </div>
            <div style={ui.mFooter}>
              <button onClick={() => { setModal(null); setError(''); }} style={ui.btnCancel}>انصراف</button>
              {vbf.can('BTNOK') && <button onClick={save} disabled={saving} style={ui.btnSave}>{saving ? '⏳ ...' : '💾 ذخیره'}</button>}
            </div>
          </div>
        </div>
      )}

      {modal === 'delete' && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: '460px' }} onClick={e => e.stopPropagation()}>
            <div style={{ ...ui.mHeader, background: 'linear-gradient(135deg,#b71c1c,#c62828)' }}>
              <div style={ui.mTitle}>⚠️ تأیید حذف</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={{ ...ui.mBody, textAlign: 'center', padding: '26px' }}>
              <div style={{ fontSize: '34px', marginBottom: '10px' }}>🗑️</div>
              {selected?.department}<br />{selected?.credit_title}<br />{selected?.program}
            </div>
            <div style={ui.mFooter}>
              <button onClick={() => setModal(null)} style={ui.btnCancel}>خیر</button>
              {vbf.can('BTNDELETE') && <button onClick={doDelete} disabled={saving} style={{ ...ui.btnSave, background: '#b71c1c' }}>{saving ? '⏳...' : 'بله، حذف'}</button>}
            </div>
          </div>
        </div>
      )}

      {toast && <div style={ui.toast}>{toast}</div>}
    </>
  );
}
